import { CharacterDefinition } from '@hexcrawl/shared-utils';
import { ArtManifest } from './types.ts';
import { resolveLayerFiles } from './layerResolver.ts';

// ---------------------------------------------------------------------------
// Image loading with module-level cache
// ---------------------------------------------------------------------------

const IMAGE_CACHE = new Map<string, ImageBitmap>();

async function loadImage(url: string): Promise<ImageBitmap> {
  const cached = IMAGE_CACHE.get(url);
  if (cached) return cached;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load layer PNG: ${url} (${res.status})`);
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);
  IMAGE_CACHE.set(url, bitmap);
  return bitmap;
}

// ---------------------------------------------------------------------------
// Tinting
// ---------------------------------------------------------------------------

function parseHexColor(hex: string): { r: number; g: number; b: number } {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

// Multiply-tint: output = (sprite_channel * tint_channel) / 255.
// Matches the server's sharp-based implementation exactly. Alpha is unchanged.
function applyMultiplyTint(
  bitmap: ImageBitmap,
  tint: { r: number; g: number; b: number },
): ImageBitmap {
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0);
  const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    data[i]     = Math.round(data[i]     * tint.r / 255);
    data[i + 1] = Math.round(data[i + 1] * tint.g / 255);
    data[i + 2] = Math.round(data[i + 2] * tint.b / 255);
    // data[i + 3] (alpha) left unchanged
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.transferToImageBitmap();
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

/**
 * Composite all layers for an animation folder into a single spritesheet ImageBitmap.
 * Equivalent to the server's assembleAnimation().
 */
export async function assembleAnimation(
  animFolder: string,
  definition: CharacterDefinition,
  manifest: ArtManifest,
): Promise<ImageBitmap> {
  const layers = resolveLayerFiles(animFolder, definition, manifest);
  if (layers.length === 0) {
    throw new Error(`No layers resolved for animation "${animFolder}".`);
  }

  // Determine canvas dimensions from the first layer
  const firstBitmap = await loadImage(layers[0].imageUrl);
  const canvas = new OffscreenCanvas(firstBitmap.width, firstBitmap.height);
  const ctx = canvas.getContext('2d')!;

  for (const layer of layers) {
    let bitmap = await loadImage(layer.imageUrl);
    if (layer.tint) {
      bitmap = applyMultiplyTint(bitmap, parseHexColor(layer.tint));
    }
    ctx.drawImage(bitmap, 0, 0);
  }

  return canvas.transferToImageBitmap();
}
